import { useState } from 'react'
import { useEmail, useEmailThread } from '@/hooks/use-api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Inbox, Send, Paperclip, Mail, Calendar, User, Reply } from 'lucide-react'
import { ComposeEmailModal } from '@/components/ComposeEmailModal'
import type { Email } from '@/types'
import { toDisplayDate } from '@/lib/utils'

interface EmailDetailModalProps {
  emailId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EmailDetailModal({ emailId, open, onOpenChange }: EmailDetailModalProps) {
  const { data: emailData, isLoading: emailLoading } = useEmail(emailId || '')
  const { data: threadData, isLoading: threadLoading } = useEmailThread(emailId || '')
  const [replyModalOpen, setReplyModalOpen] = useState(false)

  const email = emailData as Email | undefined
  const thread = (threadData as Email[] | undefined) || []

  const formatDate = (date: string | undefined) => {
    if (!date) return '-'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const renderEmailContent = (emailItem: Email) => {
    // Prefer HTML body, fall back to text body
    const content = emailItem.html_body || emailItem.text_body

    if (!content) {
      return <p className="text-muted-foreground italic">No content available</p>
    }

    if (emailItem.html_body) {
      return (
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: emailItem.html_body }}
        />
      )
    }

    return <pre className="whitespace-pre-wrap font-sans text-sm">{emailItem.text_body}</pre>
  }

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        aria-describedby="email-description"
      >
        <DialogHeader>
          <DialogTitle>{email?.subject || 'Email Details'}</DialogTitle>
          <DialogDescription id="email-description">
            View email details and conversation thread
          </DialogDescription>
        </DialogHeader>

        {emailLoading ? (
          <div className="flex justify-center py-8" role="status" aria-live="polite">
            <p>Loading email...</p>
          </div>
        ) : !email ? (
          <div className="text-center py-8 text-muted-foreground">Email not found</div>
        ) : (
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Email Details</TabsTrigger>
              <TabsTrigger value="thread">Thread ({thread.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-6 mt-4">
              {/* Header Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {email.incoming ? (
                      <Badge variant="secondary" className="gap-1">
                        <Inbox className="h-3 w-3" aria-hidden="true" />
                        Incoming
                      </Badge>
                    ) : (
                      <Badge variant="default" className="gap-1">
                        <Send className="h-3 w-3" aria-hidden="true" />
                        Outgoing
                      </Badge>
                    )}
                    {email.has_attachment && (
                      <Badge variant="outline" className="gap-1">
                        <Paperclip className="h-3 w-3" aria-hidden="true" />
                        Has Attachment
                      </Badge>
                    )}
                    {email.is_opened && <Badge variant="success">Opened</Badge>}
                    {email.is_bounced && <Badge variant="destructive">Bounced</Badge>}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReplyModalOpen(true)}
                    aria-label={`Reply to email: ${email.subject || 'No subject'}`}
                  >
                    <Reply className="mr-2 h-4 w-4" aria-hidden="true" />
                    Reply
                  </Button>
                </div>

                <dl className="grid grid-cols-1 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" aria-hidden="true" />
                      Subject
                    </dt>
                    <dd className="text-base font-semibold mt-1">
                      {email.subject || '(No subject)'}
                    </dd>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <User className="h-4 w-4" aria-hidden="true" />
                        From
                      </dt>
                      <dd className="text-sm mt-1">
                        {email.from_name && <div className="font-medium">{email.from_name}</div>}
                        <div className="text-muted-foreground">{email.from_address || '-'}</div>
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <User className="h-4 w-4" aria-hidden="true" />
                        To
                      </dt>
                      <dd className="text-sm mt-1 text-muted-foreground">
                        {email.to_address || '-'}
                      </dd>
                    </div>
                  </div>

                  {email.cc_address && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">CC</dt>
                      <dd className="text-sm mt-1 text-muted-foreground">{email.cc_address}</dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" aria-hidden="true" />
                      Date
                    </dt>
                    <dd className="text-sm mt-1">{formatDate(email.message_date)}</dd>
                  </div>

                  {email.is_opened && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">First Opened</dt>
                        <dd className="text-sm mt-1">{formatDate(email.first_opened_date)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Last Opened</dt>
                        <dd className="text-sm mt-1">{formatDate(email.last_opened_date)}</dd>
                      </div>
                    </div>
                  )}
                </dl>
              </div>

              <Separator />

              {/* Email Content */}
              <div>
                <h3 className="text-sm font-medium mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Email Content
                </h3>
                <div className="border rounded-lg p-4 bg-muted/50">{renderEmailContent(email)}</div>
              </div>
            </TabsContent>

            <TabsContent value="thread" className="space-y-4 mt-4">
              {threadLoading ? (
                <div className="flex justify-center py-8" role="status" aria-live="polite">
                  <p>Loading thread...</p>
                </div>
              ) : thread.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No thread available</div>
              ) : (
                <div className="space-y-4">
                  {thread.map((threadEmail, index) => (
                    <div
                      key={threadEmail.id}
                      className={`border rounded-lg p-4 ${
                        threadEmail.id === email.id ? 'bg-accent/50 border-primary' : 'bg-card'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {threadEmail.incoming ? (
                              <Inbox className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                            ) : (
                              <Send className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                            )}
                            <span className="font-medium">
                              {threadEmail.from_name || threadEmail.from_address}
                            </span>
                            {threadEmail.id === email.id && (
                              <Badge variant="outline" className="text-xs">
                                Current
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(threadEmail.message_date)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">
                            Email {index + 1} of {thread.length}
                          </div>
                        </div>
                      </div>

                      <Separator className="my-3" />

                      <div className="text-sm font-medium mb-2">
                        {threadEmail.subject || '(No subject)'}
                      </div>

                      <div className="text-sm">{renderEmailContent(threadEmail)}</div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>

      {/* Reply Compose Modal */}
      <ComposeEmailModal open={replyModalOpen} onOpenChange={setReplyModalOpen} replyTo={email} />
    </Dialog>
  )
}
